import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import { theme } from '../muiTheme';
import { useState, useEffect, useRef } from 'react';
import { useClearableTimeout } from '../../helpers/timers';

/**
 * A reusable text box component that can be used for various text inputs.
 *
 * @param {object} SearchBarProperties
 * @param {function} SearchBarProperties.onChange Callback (event) => {} to run on change
 * @param {function} SearchBarProperties.onTimedCallback Callback (event) => {} that will run with the change event, but only when the user stops inputting for a half second, also runs on clear 
 * @param {function} SearchBarProperties.InputProps.style Custom styling for the input
 * @param {boolean} [showSearchResults=false] Flag to show search results dropdown
 * @param {Array} [searchResults=[{id, name}]] Array of search results to display in the dropdown
 * @param {function} [selectItemFunc=() => {}] Callback function to run when a search result is selected
 * @param {function} [onFocusFunc=null] Callback function to run when the search bar is focused
 * @returns {JSX.Element} A search bar component.
*/

export default function SearchBar({SearchBarProperties, showSearchResults = false, searchResults = [], selectItemFunc = () => {}, showSearchIcon = true, onFocusFunc = null}) {
    const [searchTerm, setSearchTerm] = useState("");
    const [results, setResults] = useState(searchResults); // Local state for search results
    const timeoutRef = useRef(null);
    const blankFunction = () => {};
    const clearAndInitiateTimedCallback = useClearableTimeout(SearchBarProperties?.onTimedCallback || blankFunction, 500);

    useEffect(() => {
        // Update the results when searchResults prop changes
        setResults(searchResults);
    }, [searchResults]); 

    useEffect(() => {
        return () => {
            clearTimeout(timeoutRef.current);
        };
    }, []);

    function handleChange(event) {
        //run the custom onChange callback if present
        if (SearchBarProperties?.onChange) {
            try {
                SearchBarProperties.onChange(event);
            } catch (e) {
                console.error(e);
            }
        }

        //if a timed callback is present, run it
        if (SearchBarProperties?.onTimedCallback) {
            try {
                clearAndInitiateTimedCallback(event);
            } catch (e) {
                console.error(e);
            }
        }
        setSearchTerm(event.target.value);
    }

    // Function to clear the search term
    const handleClearSearch = () => {
        handleChange({ target: { value: "" } });
    };

    return (
        <>
            <FormControl
                sx = {{
                    width: "100%",
                    height: "100%"
                }}
            >
                <TextField autoComplete="off" spellCheck={false} // Disable browser autocomplete and spellcheck
                    placeholder={SearchBarProperties?.placeholder || 'Search'}
                    value={searchTerm}
                    onChange={handleChange}
                    onBlur={() => {
                        timeoutRef.current = setTimeout(() => {
                            setResults([]); // Clear the search results
                        }, 200); // Delay clearing to allow onClick to execute
                    }}
                    onFocus={onFocusFunc}
                    InputProps={{
                        style: {
                            fontFamily: 'Lato',
                            fontSize: '14px',
                            fontStyle: 'normal',
                            fontWeight: '400',
                            lineHeight: '17px',
                            ...SearchBarProperties?.InputProps?.style
                        },
                        endAdornment: (
                            <InputAdornment 
                                position="end" 
                                onClick={searchTerm ? handleClearSearch : undefined}
                                sx={{
                                    color: "var(--Search-Icon, #6F8090)", 
                                    opacity: '50%',
                                    zIndex: 2, // Added z-index, this makes the icon shown when hover or focus on the search bar
                                    cursor: searchTerm ? "pointer" : "default",
                                }}>
                                {searchTerm ?  // Show "x" if there's a search term, otherwise show search icon
                                    <CloseIcon sx = {{width: "14px", height: "14px"}}/> : 
                                    showSearchIcon ? <SearchIcon sx = {{width: "14px", height: "14px"}}/> : null
                                }
                            </InputAdornment>
                        ),

                    }}
                    sx={{
                        borderRadius: "5px",
                        backgroundColor: SearchBarProperties.backgroundColor || 'var(--Tertiary-100, #EBF8FF)',
                        border: SearchBarProperties.border || null,
                        "& .MuiInputBase-root": {
                            padding: SearchBarProperties.InputProps?.inputPadding || "2px 12px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "10px",
                            alignSelf: "stretch",
                        },
                        "& .MuiInputBase-input": {
                            zIndex: 2, // Added z-index, this makes the input text shown when hover or focus on the search bar
                            padding: "0px",
                            ...theme.dropdownButton.font,
                            height: SearchBarProperties.InputProps?.inputHeight || "17px",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                            border: "0px",
                        },
                        "& .MuiOutlinedInput-root": {
                            "&:hover fieldset": {
                                border: "1px solid #00A6FF", // hover border color
                                backgroundColor: "var(--Neutrals-100, #FFF)"
                            },
                            "&.Mui-focused fieldset": {
                                border: "1px solid #00A6FF", // focused border color
                                backgroundColor: "var(--Neutrals-100, #FFF)"
                            },
                        },
                    }}
                />
            </FormControl>
            {showSearchResults && results.length > 0 && (
                <List
                    sx={{
                        padding: SearchBarProperties.ListProps?.padding || "4px 0px", 
                        backgroundColor: "#FFF", 
                        borderRadius: "4px", 
                        boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)", 
                        height: SearchBarProperties.ListProps?.height || "auto",
                        overflowY: "auto", // Enable vertical scrolling
                    }}
                >  
                    {results.map(result => (
                        <ListItem 
                            onClick={() => {
                                if(searchResults.length === 1 && searchResults[0].id === "noResults"){
                                    setSearchTerm(""); // Clear the search term
                                }else{
                                    setSearchTerm(result.name); // Set the search term to the selected result's name
                                }; 
                                setResults([]); // Clear the search results after selection
                                selectItemFunc(result); // Call the selectItemFunc with the selected result
                            }}
                            key={result.id} 
                            sx={{ 
                                padding: "2px 5px",
                                "&:hover": {
                                    backgroundColor: "var(--Tertiary-100, #EBF8FF)", // Set background color on hover
                                }, 
                            }}
                        >
                            <ListItemText
                                primary={result.name}
                                sx={{
                                    margin: 0,
                                    color: "var(--Neutrals-900, #6F8090)",
                                    fontFamily: "Lato",
                                    fontSize: "14px",
                                    fontStyle: "normal",
                                    fontWeight: "700",
                                    lineHeight: "normal",
                                    whiteSpace: "nowrap", // Prevent text wrapping
                                    overflow: "hidden", // Hide overflowed text
                                    textOverflow: "ellipsis", // Add ellipsis for truncated text
                                }}
                            />
                        </ListItem>
                    ))}
                </List>
            )}
        </>
    )
}